import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { BOLD_TEXT, COLOR_TEXT, SIZE_TEXT } from "@/comn/features/foundation";
import { comnUtils } from "@/comn/utils";
import { UseOptionsProps } from "@/comn/hooks";
import { useOptions_v2 } from "@/comn/hooks/useOptions.v2";

/** */
type CheckboxProps = UseOptionsProps & {
    edit?: boolean;
    mode?: "edit" | "view" | null;
    all?: boolean;
    checkAll?: boolean;
    viewType?: "label" | "value" | "both";
    editType?: "label" | "value" | "both";

    name?: string;
    value?: any[];
    readOnly?: boolean | ((...args: any) => boolean);
    disabled?: boolean;

    color?: keyof typeof COLOR_TEXT;
    editColor?: keyof typeof COLOR_TEXT;
    bold?: keyof typeof BOLD_TEXT;
    editBold?: keyof typeof BOLD_TEXT;
    fontSize?: keyof typeof SIZE_TEXT;
    align?: "start" | "left" | "center" | "right" | "end";

    onBlur?: (arg?: any) => void;
    onChange?: (arg?: any) => void;
};

export const Checkbox = (props: CheckboxProps) => {
    const {
        /** */
        all,
        checkAll,
        edit = true,
        mode,
        viewType = "both",
        editType = "label",
        align,

        /**  */
        value,
        onChange,

        /** input props */
        name,
        readOnly,
        disabled,
        onBlur,

        // For style
        color,
        bold,
        editColor,
        editBold,
        fontSize,
        // For useOptions
        options: propsOptions,
        area,
        comnCd,
        excludes,
        includes,
        filter,
    } = props;

    console.log(align);

    const _props = Object.fromEntries(
        Object.entries({
            name,
            disabled,
            onBlur,
        }).filter(([, value]) => value !== undefined),
    );

    const { t } = useTranslation();
    const { options, getListItemKey } = useOptions_v2({
        propsOptions,
        comnCd,
        area,
        excludes,
        includes,
        filter,
    });

    /** handle change */
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = formatCheckbox(value);
        const nextValue = event.target.checked
            ? [...formattedValue, event.target.value]
            : formattedValue.filter((item) => item !== event.target.value);

        if (onChange) {
            onChange(nextValue);
        }
    };

    /** handle change all */
    const handleChangeAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        let next: any[] = [];

        if (event.target.checked) {
            next = options.map(({ value }) => value);
        }

        if (onChange) {
            onChange(next);
        }
    };
    const hiddenInput = mode === "view" ? true : mode === "edit" ? false : !edit;
    const formattedValue = formatCheckbox(value);
    const viewValue = viewCheckbox(value, { viewType, options });

    return (
        <div className="w-full">
            {/* view text */}
            {hiddenInput && (
                <div
                    title={viewCheckbox(value, { viewType, options }, true)}
                    className={classNames("w-fit", comnUtils.getViewStyle(color, bold, fontSize))}
                >
                    {viewValue}
                </div>
            )}
            <div hidden={hiddenInput}>
                <div
                    className={classNames("flex flex-wrap w-fit", readOnly === true && "pointer-events-none", {
                        "justify-center": align === "center",
                        "justify-start": align === "left",
                        "justify-end": align === "right",
                    })}
                >
                    {all && (
                        <div className="flex items-center h-7 space-x-1 mr-3">
                            <label className="flex items-center h-7 space-x-1">
                                <input
                                    type="checkbox"
                                    disabled={disabled}
                                    onChange={handleChangeAll}
                                    checked={options.every(({ value }) => formattedValue.includes(value)) || false}
                                />
                                <div className={comnUtils.getEditStyle(editColor, editBold)}>{t(`L_AL`)}</div>
                            </label>
                        </div>
                    )}
                    {options.map((item, index) => {
                        const itemReadOnly = readOnly instanceof Function ? readOnly(item) : readOnly;

                        return (
                            <label
                                key={getListItemKey(item, index)}
                                className={classNames(
                                    "flex items-center h-7 space-x-1 mr-3",
                                    itemReadOnly && "pointer-events-none",
                                )}
                            >
                                <input
                                    {..._props}
                                    readOnly={itemReadOnly}
                                    type="checkbox"
                                    value={item.value}
                                    onChange={handleChange}
                                    checked={formattedValue.some((_) => _ === item.value)}
                                />
                                {item.label && (
                                    <div className={comnUtils.getEditStyle(editColor, editBold)}>
                                        {editCheckbox(item.value, t(item.label), editType)}
                                    </div>
                                )}
                            </label>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export const viewCheckbox = (v: any, o?: any, all?: boolean) => {
    if (!o?.options) return;
    if (!v) return;

    const view = o.options
        .filter(({ value }: any) => {
            return v.includes(value);
        })
        .map(({ label, value }: any) => {
            const vt = value || "";
            const lt = label || "";
            const vtWithBracket = value ? `[${value}] ` : "";

            let view = o?.viewType === "value" ? vt : o?.viewType === "label" ? lt : vtWithBracket + lt;
            if (all === true) {
                view = vtWithBracket + lt;
            }

            return view;
        })
        .join(", ");

    return view;
};

export const editCheckbox = (value: string, label: string, editType: string) => {
    const view = editType === "label" ? label : editType === "value" ? value : "[" + value + "] " + label;
    return view;
};

export const formatCheckbox = (v: any) => {
    if (!Array.isArray(v)) return [];
    return v;
};

export const unformatCheckbox = (v: any, o?: any) => {
    if (!Array.isArray(v)) return undefined;
    if (v.length === 0) return [];
    return v;
};
